import React, { useState, useEffect, useRef, useCallback } from 'react';
import Draggable from 'react-draggable';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { Mic, MicOff, Loader2, Square, X, Stethoscope, Send, ChevronRight } from 'lucide-react';

/**
 * DictationWidget — Floating pill with two modes:
 *
 *  DICTATE mode (default): click mic → streams to Deepgram → inserts into focused field.
 *  AI mode: type a command → AI routes / acts.
 *
 * Mode switch: right-click anywhere on the widget, or click the ⇄ toggle icon.
 * Keyboard: Shift+Alt = dictate toggle.   ⌘⇧A = open AI mode.
 */

const STATES = { IDLE: 'idle', CONNECTING: 'connecting', LISTENING: 'listening', PROCESSING: 'processing' };
const MODE = { DICTATE: 'dictate', AI: 'ai' };

const QUICK_CMDS = ['split by date', 'route document', 'message nurse', 'schedule follow-up'];

export default function DictationWidget() {
  // ── Shared ──────────────────────────────────────────────────
  const [mode, setMode]       = useState(MODE.DICTATE);
  const [visible, setVisible] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const nodeRef = useRef(null);
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── Dictation state ─────────────────────────────────────────
  const [dictState, setDictState] = useState(STATES.IDLE);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim]       = useState('');
  const wsRef            = useRef(null);
  const mediaStreamRef   = useRef(null);
  const mediaRecorderRef = useRef(null);
  const targetElementRef = useRef(null);
  const transcriptRef    = useRef('');
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);

  // Track last focused text input
  useEffect(() => {
    const handleFocusIn = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && el.type !== 'checkbox' && el.type !== 'radio') || el.isContentEditable)) {
        if (!el.closest('[data-testid="dictation-widget"]') && !el.closest('[data-testid="dictation-widget-minimized"]')) {
          targetElementRef.current = el;
        }
      }
    };
    document.addEventListener('focusin', handleFocusIn, true);
    return () => document.removeEventListener('focusin', handleFocusIn, true);
  }, []);

  // ── AI Command state ─────────────────────────────────────────
  const [aiInput, setAiInput]   = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const aiInputRef = useRef(null);

  // Focus AI input when switching to AI mode
  useEffect(() => {
    if (mode === MODE.AI && !minimized) {
      setTimeout(() => aiInputRef.current?.focus(), 50);
    }
  }, [mode, minimized]);

  // ── Keyboard shortcuts ──────────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      // Shift+Alt → dictation toggle
      if (e.shiftKey && e.altKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setMode(MODE.DICTATE);
        setMinimized(false);
        handleDictToggle();
      }
      // ⌘⇧A → AI mode
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setMode(MODE.AI);
        setMinimized(false);
      }
      if (e.key === 'Escape') setAiResult(null);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []); // eslint-disable-line

  // ── Right-click context menu ─────────────────────────────────
  const handleContextMenu = (e) => {
    e.preventDefault();
    setMode(m => m === MODE.DICTATE ? MODE.AI : MODE.DICTATE);
    setAiResult(null);
  };

  // ── Dictation helpers ────────────────────────────────────────
  const insertText = useCallback((text) => {
    if (!text) return;
    try { navigator.clipboard.writeText(text); } catch {}
    const el = targetElementRef.current;
    if (!el) return;
    try {
      el.focus();
      setTimeout(() => {
        try {
          if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
            const start = el.selectionStart ?? el.value.length;
            const end   = el.selectionEnd   ?? el.value.length;
            const nv    = el.value.substring(0, start) + text + el.value.substring(end);
            const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
            const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
            setter ? setter.call(el, nv) : (el.value = nv);
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el.dispatchEvent(new Event('change', { bubbles: true }));
            el.selectionStart = el.selectionEnd = start + text.length;
          } else if (el.isContentEditable) {
            const sel = window.getSelection();
            if (sel && sel.rangeCount) {
              sel.deleteFromDocument();
              sel.getRangeAt(0).insertNode(document.createTextNode(text));
            }
          }
        } catch {}
      }, 50);
    } catch {}
  }, []);

  const stopRecording = useCallback(() => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    try { mediaStreamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    try { if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close(); } catch {}
    wsRef.current = null; mediaStreamRef.current = null; mediaRecorderRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    setDictState(STATES.CONNECTING);
    setTranscript(''); setInterim('');
    try {
      const tokenRes = await api.get('/voice/dictation-token');
      const { token, url } = tokenRes.data;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const ws = new WebSocket(`${url}?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setDictState(STATES.LISTENING);
        const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
        mediaRecorderRef.current = mr;
        mr.ondataavailable = (e) => { if (e.data.size > 0 && ws.readyState === WebSocket.OPEN) ws.send(e.data); };
        mr.start(250);
      };
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          const alt = d?.channel?.alternatives?.[0];
          if (!alt) return;
          if (d.is_final) { setTranscript(t => t + alt.transcript + ' '); setInterim(''); }
          else setInterim(alt.transcript);
        } catch {}
      };
      ws.onerror = () => { stopRecording(); setDictState(STATES.IDLE); };
      ws.onclose = () => { if (dictState === STATES.LISTENING) setDictState(STATES.IDLE); };
    } catch (err) {
      stopRecording(); setDictState(STATES.IDLE);
    }
  }, [stopRecording, dictState]);

  const handleDictStop = useCallback(() => {
    setDictState(STATES.PROCESSING);
    stopRecording();
    setTimeout(() => {
      if (transcriptRef.current) insertText(transcriptRef.current);
      setTranscript(''); setInterim(''); setDictState(STATES.IDLE);
    }, 800);
  }, [stopRecording, insertText]);

  const handleDictToggle = useCallback(() => {
    if (dictState === STATES.IDLE) startRecording();
    else if (dictState === STATES.LISTENING) handleDictStop();
  }, [dictState, startRecording, handleDictStop]);

  // ── AI Command handler ────────────────────────────────────────
  const handleAiSubmit = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    setAiResult(null);
    const cmd = aiInput.toLowerCase();
    try {
      // Try to call a context-aware endpoint
      const res = await api.post('/ai/command', { command: aiInput.trim(), context: location.pathname });
      setAiResult(res.data);
    } catch {
      // Fallback: local keyword routing
      const actions = [];
      if (cmd.includes('document') || cmd.includes('doc') || cmd.includes('route') || cmd.includes('split') || cmd.includes('file')) {
        actions.push({ label: 'Open Document Manager', nav: '/doc-manager', description: 'Review and route documents' });
      }
      if (cmd.includes('message') || cmd.includes('nurse') || cmd.includes('send')) {
        actions.push({ label: 'Open Messages', nav: '/nurse-messages', description: 'Send message to nursing staff' });
      }
      if (cmd.includes('schedule') || cmd.includes('appointment') || cmd.includes('book')) {
        actions.push({ label: 'Open Schedule', nav: '/schedule', description: 'View and manage appointments' });
      }
      if (cmd.includes('my day') || cmd.includes('queue') || cmd.includes('lab') || cmd.includes('task')) {
        actions.push({ label: 'Open My Day', nav: '/', description: 'View your queue and pending items' });
      }
      setAiResult({ response: actions.length ? `For "${aiInput}":` : `I heard "${aiInput}" — try: route document, message nurse, schedule, or open my day.`, actions });
    }
    setAiLoading(false);
  };

  const executeAction = (action) => {
    if (action.nav) navigate(action.nav);
    setAiResult(null); setAiInput('');
  };

  if (!visible) return null;

  // ── Minimized ────────────────────────────────────────────────
  if (minimized) {
    return (
      <Draggable nodeRef={nodeRef} bounds="parent" defaultPosition={{ x: 0, y: 0 }}>
        <div ref={nodeRef} className="fixed bottom-4 right-4 z-[9999]" data-testid="dictation-widget-minimized">
          <button
            onClick={() => setMinimized(false)}
            onContextMenu={handleContextMenu}
            className={`w-11 h-11 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 cursor-grab active:cursor-grabbing
              ${mode === MODE.AI ? 'bg-slate-900' : 'bg-slate-800'}`}
            title={mode === MODE.AI ? 'AI Assistant (click to expand)' : 'Dictation (click to expand)'}>
            {mode === MODE.AI
              ? <Stethoscope className="w-4 h-4 text-emerald-400" />
              : <Mic className="w-4 h-4 text-white" />}
          </button>
        </div>
      </Draggable>
    );
  }

  const isListening = dictState === STATES.LISTENING;
  const isTranscribing = dictState === STATES.CONNECTING || dictState === STATES.PROCESSING;
  const showTranscript = mode === MODE.DICTATE && (isListening || transcript || interim);
  const showAiPanel   = mode === MODE.AI;
  const widthExpanded = showTranscript || showAiPanel;

  return (
    <Draggable nodeRef={nodeRef} bounds="parent" handle=".drag-handle" defaultPosition={{ x: 0, y: 0 }}>
      <div ref={nodeRef}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999]"
        data-testid="dictation-widget"
        onContextMenu={handleContextMenu}
        style={{ width: widthExpanded ? '380px' : '200px', transition: 'width 0.2s ease' }}>
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">

          {/* ── Transcript preview (dictate mode) ── */}
          {showTranscript && (
            <div className="px-3 py-2 max-h-24 overflow-y-auto border-b border-slate-100 bg-slate-50/50"
              data-testid="dictation-transcript-preview">
              <p className="text-xs text-slate-700 leading-relaxed">
                {transcript}
                {interim && <span className="text-slate-400 italic"> {interim}</span>}
                {!transcript && !interim && <span className="text-slate-300">Listening...</span>}
              </p>
            </div>
          )}

          {/* ── AI Command panel ── */}
          {showAiPanel && (
            <div className="border-b border-slate-100">
              {/* Input row */}
              <div className="flex items-center gap-1.5 px-2.5 py-2">
                <input
                  ref={aiInputRef}
                  value={aiInput}
                  onChange={e => setAiInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAiSubmit()}
                  placeholder="Ask anything..."
                  className="flex-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  data-testid="ai-command-input"
                />
                <button onClick={handleAiSubmit} disabled={aiLoading || !aiInput.trim()}
                  className="w-7 h-7 rounded-lg bg-slate-900 flex items-center justify-center text-white disabled:opacity-40 transition-opacity"
                  data-testid="ai-command-submit">
                  {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                </button>
              </div>
              {/* Quick command chips */}
              {!aiResult && (
                <div className="px-2.5 pb-2 flex gap-1 flex-wrap">
                  {QUICK_CMDS.map(q => (
                    <button key={q} onClick={() => setAiInput(q)}
                      className="text-[9px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors">
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {/* Results */}
              {aiResult && (
                <div className="px-2.5 pb-2.5">
                  {aiResult.response && (
                    <p className="text-[10px] text-slate-500 mb-1.5 italic">{aiResult.response}</p>
                  )}
                  {(aiResult.actions || []).map((action, i) => (
                    <button key={i} onClick={() => executeAction(action)}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 mb-1 rounded-lg bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-200 text-left transition-all"
                      data-testid={`ai-action-${i}`}>
                      <ChevronRight className="w-3 h-3 text-emerald-500 shrink-0" />
                      <div>
                        <p className="text-[11px] font-medium text-slate-700">{action.label}</p>
                        {action.description && <p className="text-[9px] text-slate-400">{action.description}</p>}
                      </div>
                    </button>
                  ))}
                  <button onClick={() => { setAiResult(null); setAiInput(''); }}
                    className="text-[9px] text-slate-400 hover:text-slate-600 mt-0.5">
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Control bar ── */}
          <div className="flex items-center gap-2 px-3 py-2 drag-handle cursor-grab active:cursor-grabbing select-none">

            {/* Mode toggle: Mic ⇄ AI */}
            <div className="flex items-center rounded-lg bg-slate-100 p-0.5 gap-0.5">
              <button
                onClick={() => { setMode(MODE.DICTATE); setAiResult(null); }}
                className={`w-6 h-6 rounded-md flex items-center justify-center transition-all
                  ${mode === MODE.DICTATE ? 'bg-white shadow-sm text-slate-700' : 'text-slate-400 hover:text-slate-600'}`}
                title="Dictation mode (Shift+Alt)"
                data-testid="mode-dictate-btn">
                <Mic className="w-3 h-3" />
              </button>
              <button
                onClick={() => { setMode(MODE.AI); }}
                className={`w-6 h-6 rounded-md flex items-center justify-center transition-all
                  ${mode === MODE.AI ? 'bg-slate-900 shadow-sm text-emerald-400' : 'text-slate-400 hover:text-slate-600'}`}
                title="AI Command mode (⌘⇧A)"
                data-testid="mode-ai-btn">
                <Stethoscope className="w-3 h-3" />
              </button>
            </div>

            {/* Dictate: mic action button */}
            {mode === MODE.DICTATE && (
              <>
                <button onClick={handleDictToggle}
                  disabled={isTranscribing}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium text-white transition-all
                    ${isListening ? 'bg-rose-600 animate-pulse' : isTranscribing ? 'bg-blue-600' : 'bg-slate-800 hover:bg-slate-700'}`}
                  data-testid="dictation-toggle-btn"
                  title={dictState === STATES.IDLE ? 'Start dictation (Shift+Alt)' : 'Stop'}>
                  {isTranscribing ? <Loader2 className="w-3 h-3 animate-spin" /> : isListening ? <Square className="w-3 h-3" /> : <Mic className="w-3 h-3" />}
                  {isListening ? 'Listening' : isTranscribing ? '...' : 'Dictate'}
                </button>
                {isListening && (
                  <div className="flex items-center gap-0.5 ml-0.5">
                    {[0,1,2,3].map(i => (
                      <div key={i} className="w-0.5 bg-rose-400 rounded-full animate-pulse"
                        style={{ height: `${8 + Math.random()*8}px`, animationDelay: `${i*0.1}s` }} />
                    ))}
                  </div>
                )}
                {!isListening && !isTranscribing && (
                  <span className="text-[9px] text-slate-300 ml-auto">Shift+Alt</span>
                )}
              </>
            )}

            {/* AI mode: label */}
            {mode === MODE.AI && (
              <span className="text-[11px] font-medium text-slate-600">
                AI Assistant <span className="text-[9px] text-slate-300 ml-1">⌘⇧A</span>
              </span>
            )}

            {/* Right-click hint */}
            <span className="text-[8px] text-slate-200 ml-auto hidden sm:block" title="Right-click to switch mode">RC=switch</span>

            {/* Minimize */}
            <button onClick={() => setMinimized(true)}
              className="p-1 rounded hover:bg-slate-100 text-slate-400 transition-colors"
              title="Minimize">
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </Draggable>
  );
}
